'use client'

// Since you were here.
//
// The one thing a returning person wants to know before anything else:
// did any of it land. He kept something of yours, somebody liked what
// you left, he said your name in his diary, a thing he did came from
// you. Until now that answer was spread over three drawers and a dot,
// and the ordinary case (he read your offering an hour after you closed
// the tab) had no surface at all.
//
// One call on arrival (/api/since stamps the visit and answers for the
// gap), one card under the needs bar, plain words, two doors: read what
// he wrote, or see the talk. Nothing if nothing happened, nothing on a
// reload inside the same sitting, nothing on a first visit.

import { useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { useAccount, authFetch } from '../auth/useUser'
import { CloseButton, KawaiiCta, kawaiiPanel } from './KawaiiButton'
import { ui, kawaii } from '../../lib/ui/theme'
import { sinceLines } from '../../lib/ui/since'

export function SinceStrip() {
  const { ready, signedIn } = useAccount()
  const [data, setData] = useState(null)
  const [gone, setGone] = useState(false)
  const asked = useRef(false)
  const setProfileTab = useGameStore((s) => s.setProfileTab)
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)
  const setShrineOpen = useGameStore((s) => s.setShrineOpen)

  useEffect(() => {
    if (!ready || !signedIn || asked.current) return
    asked.current = true
    authFetch('/api/since', { method: 'POST' })
      .then((r) => r.json())
      .then((d) => { if (d && !d.quiet) setData(d) })
      .catch(() => { /* a strip is not worth a retry */ })
  }, [ready, signedIn])

  if (!data || gone) return null
  const lines = sinceLines(data)
  if (!lines.length) return null

  const readIt = () => { setGone(true); setProfileTab('mentions'); setProfileOpen(true) }
  const seeTalk = () => { setGone(true); setShrineOpen(true) }
  const hasWords = (data.mentions || 0) + (data.shaped || 0) + (data.verdicts?.kept || 0) + (data.verdicts?.gone || 0) > 0
  const hasTalk = (data.talk?.likes || 0) + (data.talk?.comments || 0) > 0

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-auto w-[calc(100vw-1.5rem)] max-w-[540px]"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 96px)' }}
      role="status"
    >
      <div style={{ ...kawaiiPanel(), borderRadius: 18, padding: '10px 12px 10px 14px', fontFamily: ui.font }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p style={{ color: kawaii.inkSoft, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              since you were here
            </p>
            <p style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 700, lineHeight: 1.45, marginTop: 2 }}>
              {lines.join(', ')}.
            </p>
            {data.latest?.line && (
              <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, marginTop: 4, fontStyle: 'italic' }}>
                {`\u201c${data.latest.line}\u201d`}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 8 }}>
              {hasWords && <KawaiiCta small intent="primary" onClick={readIt}>read what he wrote</KawaiiCta>}
              {hasTalk && <KawaiiCta small onClick={seeTalk}>see the talk</KawaiiCta>}
            </div>
          </div>
          <CloseButton onClick={() => setGone(true)} />
        </div>
      </div>
    </div>
  )
}

export default SinceStrip
